// features/employee/fund/components/EmployeeFundModal.jsx
import { Col, Form, Input, InputNumber, Modal, Radio, Row, Select } from "antd";
import { useTranslation } from "react-i18next";
import BeneficiaryTable from "./BeneficiaryTable";
import EligibilityConditionFields from "./EligibilityConditionFields";
import styles from "../styles/EmployeeFund.module.css";
import { CONTRIBUTION_METHOD, MEMBERSHIP_STATUS, toOptions } from "../schemas/EmployeeFundSchemas";
import FormAntd from "components/form/FormProvide/FormAntd";
import { FormDatePicker } from "components/form/FormProvide";
import { useEffect } from "react";
import { getRelationshipOptions } from "features/masterData/services/optionsApi";

/**
 * mode: "create" | "edit" | "view"
 * fundOptions: [{ value, label, fundCode, securitiesCompany }] — from the fund master-data list
 */
const EmployeeFundModal = ({
    open,
    mode = "create",
    form,
    fundOptions = [],
    isSaving = false,
    onOk = () => { },
    onCancel = () => { },
}) => {
    const { t } = useTranslation();
    const isView = mode === "view";
    console.log("mode", mode);

    useEffect(() => {
        getRelationshipOptions();
    }, []);

    const titleMap = {
        create: t("employeeFund.label.addFund") || "เพิ่มกองทุน",
        edit: t("employeeFund.label.editFund") || "แก้ไขกองทุน",
        view: t("employeeFund.label.viewFund") || "รายละเอียดกองทุน",
    };

    const employeeMethod = Form.useWatch(["employeeContribution", "method"], form);
    const companyMethod = Form.useWatch(["companyContribution", "method"], form);

    const handleFundSelect = (fundId) => {
        const selected = fundOptions.find((f) => f.value === fundId);
        console.log("selected", form.getFieldsValue(true), selected);
        // form.setFieldValue([...namePrefix, "duration"], Math.abs(e.diff(form.getFieldValue("contractStartDate"), durationUnit == 'YEAR' ? 'year' : 'month')))
        // contributionRateCriteria
        form.setFieldsValue({
            statusFlag: selected.statusFlag || "ACTIVE",
            fundCodeDisplay: selected?.fundCode || "",
            securitiesCompanyDisplay: selected?.securitiesCompany || "",
            "membershipEligibility": {
                conditionType: selected.contributionRateCriteria || "YEARS_OF_SERVICE",
                durationUnit: selected?.contributionRateConditions?.rules?.[0]?.serviceFromUnit || "MONTH",
                duration: null,
                effectiveStartDate: null,
            },
            "contributionEligibility": {
                conditionType: selected.benefitEligibilityCriteria || "YEARS_OF_SERVICE",
                durationUnit: selected?.vestingConditions?.rules?.[0]?.serviceFromUnit || "MONTH",
                duration: null,
                effectiveStartDate: null,
            }
            // contributionRateConditions: selected?.contributionRateConditions || [],
        });
    };

    return (
        <Modal
            open={open}
            title={titleMap[mode]}
            width={960}
            onCancel={onCancel}
            onOk={() => {
                form?.submit()
            }}
            okText={t("common.save") || "บันทึก"}
            cancelText={t("common.cancel") || "ยกเลิก"}
            confirmLoading={isSaving}
            footer={isView ? null : undefined}
            destroyOnClose
        >
            <FormAntd form={form} onFinish={onOk} layout="vertical" disabled={isView}>
                {/* ---------- ข้อมูลกองทุน ---------- */}
                <div className={styles.modalSection}>
                    <div className={styles.sectionTitle}>{t("employeeFund.label.fundInfo") || "ข้อมูลกองทุน"}</div>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                label={t("fund.label.fundName") || "ชื่อกองทุน"}
                                name="fundId"
                                rules={[{ required: true, message: "" }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    options={fundOptions}
                                    placeholder={t("fund.placeholder.please_select") || "กรุณาเลือก"}
                                    onChange={handleFundSelect}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label={t("fund.label.fundCode") || "เลขที่กองทุน"} name="fundCodeDisplay">
                                <Input disabled placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={t("fund.label.securitiesCompany") || "บริษัทหลักทรัพย์"}
                                name="securitiesCompanyDisplay"
                            >
                                <Input disabled placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"} />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <FormDatePicker
                                required
                                allowClear
                                disabled={isView}
                                style={{ width: "100%" }} format="DD/MM/YYYY"
                                label={t("employeeFund.label.contractStartDate") || "วันที่เริ่มสัญญากองทุน"}
                                name="contractStartDate"
                            />
                        </Col>
                        <Col span={8}>
                            <FormDatePicker
                                // required
                                allowClear
                                disabled={isView}
                                style={{ width: "100%" }}
                                label={t("employeeFund.label.contractEndDate") || "วันที่สิ้นสุดสัญญากองทุน"}
                                name="contractEndDate"
                            />
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={t("employeeFund.label.fundStatus") || "สถานะกองทุน"}
                                name="statusFlag"
                                initialValue="ACTIVE"
                                rules={[{ required: true, message: t("employeeFund.label.fundStatus") }]}
                            >
                                <Radio.Group options={toOptions(MEMBERSHIP_STATUS)} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* ---------- เงื่อนไขสิทธิ ---------- */}
                <Row gutter={16} className={styles.modalSection}>
                    <Col span={12}>
                        <EligibilityConditionFields
                            disabled={isView}
                            namePrefix={["membershipEligibility"]}
                            title={t("employeeFund.label.membershipEligibility") || "เงื่อนไขการมีสิทธิเป็นสมาชิกกองทุน"}
                            tooltip={
                                t("employeeFund.tooltip.membershipEligibility") ||
                                "เงื่อนไขที่พนักงานต้องมีอายุงานหรืออายุสมาชิกครบตามที่กำหนด จึงจะมีสิทธิเป็นสมาชิกกองทุน"
                            }
                        />
                    </Col>
                    {/* <Col span={12}>
                        <EligibilityConditionFields
                            namePrefix={["contributionEligibility"]}
                            title={t("employeeFund.label.contributionEligibility") || "เงื่อนไขการได้รับสิทธิเงินสมทบ"}
                            tooltip={
                                t("employeeFund.tooltip.contributionEligibility") ||
                                "เงื่อนไขที่พนักงานต้องมีอายุงานหรืออายุสมาชิกครบตามที่กำหนด จึงจะมีสิทธิได้รับเงินสมทบจากบริษัท"
                            }
                        />
                    </Col> */}
                    <Col span={12}>
                        <div className={styles.modalSection}>
                            <div className={styles.sectionTitle}>
                                {t("employeeFund.label.contributionInfo") || "ข้อมูลการสะสมและสมทบ"}
                            </div>
                            <Row gutter={24}>
                                <Col span={12}>
                                    <Form.Item
                                        label={t("employeeFund.label.employeeDeductionMethod") || "วิธีหักเงินพนักงาน"}
                                        name={["employeeContribution", "method"]}
                                        initialValue="PERCENTAGE"
                                        rules={[{ required: true, message: "" }]}
                                    >
                                        <Radio.Group options={toOptions(CONTRIBUTION_METHOD)} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label={t("employeeFund.label.employeeRate") || "พนักงานสะสม"}
                                        name={["employeeContribution", "rate"]}
                                        rules={[{ required: true, message: "" }]}
                                    >
                                        <InputNumber
                                            min={0}
                                            controls={false}
                                            max={employeeMethod === "PERCENTAGE" ? 100 : undefined}
                                            style={{ width: "100%" }}
                                            addonAfter={employeeMethod === "PERCENTAGE" ? "%" : "บาท"}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label={t("employeeFund.label.companyContributionMethod") || "วิธีสมทบของบริษัท"}
                                        name={["companyContribution", "method"]}
                                        initialValue="PERCENTAGE"
                                        rules={[{ required: true, message: "" }]}
                                    >
                                        <Radio.Group options={toOptions(CONTRIBUTION_METHOD)} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label={t("employeeFund.label.companyRate") || "บริษัทสมทบ"}
                                        name={["companyContribution", "rate"]}
                                        rules={[{ required: true, message: "" }]}
                                    >
                                        <InputNumber
                                            min={0}
                                            controls={false}
                                            max={companyMethod === "PERCENTAGE" ? 100 : undefined}
                                            style={{ width: "100%" }}
                                            addonAfter={companyMethod === "PERCENTAGE" ? "%" : "บาท"}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>

                {/* ---------- ข้อมูลการสะสมและสมทบ ---------- */}
                <div className={styles.modalSection}>
                    <div style={{ color: "#98A2B3", fontSize: 12, marginTop: 4 }}>
                        {t("employeeFund.label.contributionNote") ||
                            "อัตราการหักเงินและการสมทบ อาจจะเปลี่ยนแปลงไปตามเกณฑ์กองทุนตามกฎหมายและเงื่อนไขของบริษัทกำหนด"}
                    </div>
                </div>

                {/* ---------- ผู้รับผลประโยชน์ ---------- */}
                <div className={styles.modalSection}>
                    <BeneficiaryTable disabled={isView} />
                </div>
            </FormAntd>
        </Modal>
    );
};

export default EmployeeFundModal;
